import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { SceneCard } from '@/components/SceneCard';
import { TypeTagEditor, TypeBadge } from '@/components/CharacterMeta';
import api, { getImageUrl } from '@/lib/api';
import { copyShareLink, estimateVideoCost, estimateImagesCost, formatCost } from '@/lib/generation';
import { useRates } from '@/contexts/RatesContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, UserCircle, Clapperboard, Upload, Sparkles, Trash2, ImageIcon,
  Wand2, FileText, Loader2, Users, Mail, X, Crown, Volume2, Eye, Search, Play, Check, LayoutList, Pencil, Save, Download, GripVertical, Link2, Share2, DollarSign, Lock, AlertTriangle, Tag, CheckSquare, Square, MapPin, MoreVertical, ChevronDown, Zap
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useJobs } from '@/contexts/JobsContext';

import { MediaPicker } from '@/components/MediaPicker';
import { NativeVoicePicker, VoiceDescriptionEditor } from '@/components/NativeVoice';
import { StyleSelector, styleLabel, styleThumb } from '@/components/StyleSelector';
import { CreationModeSelector } from '@/components/CreationModeSelector';
import { ProjectLocations } from '@/components/ProjectLocations';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { GettingStarted } from '@/components/GettingStarted';
import { SceneMatrix } from '@/components/SceneMatrix';
import { EntityPicker } from '@/components/EntityPicker';
import { CharacterMatchBanner } from '@/components/CharacterMatchBanner';
import { SyncBadge } from '@/components/SyncBadge';
import { LinkExistingPicker } from '@/components/LinkExistingPicker';
import { CastReviewModal } from '@/components/CastReviewModal';

const WIZARD_LABELS = ['Characters', 'Scene & Location', 'Frames', 'Audio', 'Production'];

function SortableScene({ scene, sceneNumber, projectId, characters, onDelete, onDuplicate, onUpdate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: scene.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 50 : 'auto' };
  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch gap-2">
      <button
        data-testid={`drag-scene-${scene.id}`}
        {...attributes}
        {...listeners}
        className="shrink-0 flex items-center px-1.5 rounded-lg text-[#55555C] hover:text-[#FF4500] cursor-grab active:cursor-grabbing touch-none transition-colors"
        title="Drag to reorder"
        aria-label="Drag to reorder scene"
      >
        <GripVertical className="w-5 h-5" />
      </button>
      <div className="flex-1 min-w-0">
        <SceneCard scene={scene} sceneNumber={sceneNumber} projectId={projectId} characters={characters} onDelete={onDelete} onDuplicate={onDuplicate} onUpdate={onUpdate} />
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refresh: refreshJobs } = useJobs();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [project, setProject] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(['characters', 'locations'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'scenes');
  const { rates } = useRates();
  const [costEstimate, setCostEstimate] = useState(null);
  const [shareSettings, setShareSettings] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showStyleDialog, setShowStyleDialog] = useState(false);
  const [styleDraft, setStyleDraft] = useState('photorealistic');
  const [modeDraft, setModeDraft] = useState('advanced');
  const [savingStyle, setSavingStyle] = useState(false);
  const isSimple = project?.creation_mode === 'simple';
  const openStyleDialog = () => { setStyleDraft(project?.visual_style || 'photorealistic'); setModeDraft(project?.creation_mode || 'advanced'); setShowStyleDialog(true); };
  const handleSaveStyle = async () => {
    setSavingStyle(true);
    try {
      const res = await api.put(`/projects/${id}`, { visual_style: styleDraft, creation_mode: modeDraft });
      setProject(res.data);
      setShowStyleDialog(false);
      if (modeDraft === 'simple' && (tab === 'characters' || tab === 'locations')) handleTabChange('scenes');
      toast.success(`Saved — ${styleLabel(styleDraft)} · ${modeDraft === 'simple' ? 'Simple' : 'Advanced'} mode`, { description: 'New frames, characters and videos use the new style. Existing media is unchanged until you regenerate.' });
    } catch { toast.error('Failed to update project'); }
    finally { setSavingStyle(false); }
  };
  const [sharePassword, setSharePassword] = useState('');
  const [savingShare, setSavingShare] = useState(false);
  const [shareTitle, setShareTitle] = useState('');
  const [budgetInput, setBudgetInput] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const handleTabChange = (value) => {
    setTab(value);
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', value); return p; }, { replace: true });
  };
  useEffect(() => { if (project?.creation_mode === 'simple' && tab !== 'scenes') handleTabChange('scenes'); }, [project?.creation_mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Character dialogs
  const [showCharCreate, setShowCharCreate] = useState(false);
  const [showCharImport, setShowCharImport] = useState(false);
  const [linkChar, setLinkChar] = useState(null);
  const [sceneDocBusy, setSceneDocBusy] = useState('');
  const [castReview, setCastReview] = useState(null);
  const sceneDocInputRef = useRef(null);
  const upsertCharacter = (c) => setCharacters(prev => prev.some(x => x.id === c.id) ? prev.map(x => x.id === c.id ? c : x) : [...prev, c]);
  const [charName, setCharName] = useState('');
  const [charDesc, setCharDesc] = useState('');
  const [charType, setCharType] = useState('main');
  const [charTags, setCharTags] = useState([]);
  const [charCreating, setCharCreating] = useState(false);

  // Character image generation
  const [genCharId, setGenCharId] = useState(null);
  const [genPrompt, setGenPrompt] = useState('');
  const [genLoading, setGenLoading] = useState(false);

  // Voice
  const [savingVoice, setSavingVoice] = useState(null);
  const [playingVoice, setPlayingVoice] = useState(null);
  const voiceAudioRef = useRef(null);
  const [elevenVoices, setElevenVoices] = useState([]);
  const [elevenKeyMissing, setElevenKeyMissing] = useState(false);
  const [cloningVoiceCharId, setCloningVoiceCharId] = useState(null);
  const voiceCloneInputRef = useRef(null);
  const [klingVoices, setKlingVoices] = useState([]);
  const [sceneFilter, setSceneFilter] = useState('all');
  const [expandedChars, setExpandedChars] = useState(new Set());
  const sceneStatus = (sc) => sc.video_path ? (sc.video_approved ? 'approved' : 'ready') : 'none';
  const SCENE_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'approved', label: 'Video Approved', color: '#10B981' },
    { id: 'ready', label: 'Ready', color: '#F59E0B' },
    { id: 'none', label: 'No video yet', color: '#8A8A93' },
  ];
  const visibleScenes = sceneFilter === 'all' ? scenes : scenes.filter(sc => sceneStatus(sc) === sceneFilter);
  const resumeScene = project?.current_scene_index != null && scenes[project.current_scene_index] && (project.current_scene_index > 0 || project.wizard_step > 0) ? scenes[project.current_scene_index] : null;
  const [klingKeyMissing, setKlingKeyMissing] = useState(false);
  // Turn a voices-list failure into a short message for the character card (string) or false when fine
  const errMsg = (err, fallback) => { const d = err.response?.data?.detail; return typeof d === 'string' ? (d.startsWith('MISSING_KEY:') ? d.split('|')[1] : d) : fallback; };
  const keyProblem = (e, label) => {
    const d = e.response?.data?.detail;
    if (typeof d === 'string' && d.startsWith('MISSING_KEY:')) return d.split('|')[1] || `${label} API key problem`;
    if (e.response?.status >= 500 || typeof d === 'string') return `${label} voices unavailable: ${typeof d === 'string' ? d : 'provider error'}`;
    return `Could not load ${label} voices`;
  };
  const [cloningNativeCharId, setCloningNativeCharId] = useState(null);
  const nativeCloneInputRef = useRef(null);

  // Script upload
  const [scriptUploading, setScriptUploading] = useState(false);
  const [scriptProgress, setScriptProgress] = useState('');
  const scriptInputRef = useRef(null);

  // Scene generation (bulk removed — use Production Mode for linear scene-by-scene)

  // Team management
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  // Character detail view
  const [viewCharId, setViewCharId] = useState(null);
  const viewChar = viewCharId ? characters.find(c => c.id === viewCharId) : null;
  const [editDialogName, setEditDialogName] = useState('');
  const [editDialogDesc, setEditDialogDesc] = useState('');
  const [editDialogType, setEditDialogType] = useState('main');
  const [editDialogTags, setEditDialogTags] = useState([]);
  const [savingChar, setSavingChar] = useState(false);

  // Character search + filters
  const [charSearch, setCharSearch] = useState('');
  const [charTypeFilter, setCharTypeFilter] = useState('all');
  const [charTagFilter, setCharTagFilter] = useState([]);
  // Bulk selection
  const [selectMode, setSelectMode] = useState(false);
  const [selectedCharIds, setSelectedCharIds] = useState([]);
  const [bulkTag, setBulkTag] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const rankSort = (arr) => { const r = { main: 0, background: 1, extra: 2 }; return [...arr].sort((a, b) => (r[a.character_type || 'main'] ?? 0) - (r[b.character_type || 'main'] ?? 0)); };
  const toggleSelect = (cid) => setSelectedCharIds(prev => prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]);
  const applyBulk = async (payload) => {
    if (!selectedCharIds.length) return;
    setBulkApplying(true);
    try {
      const res = await api.post(`/projects/${id}/characters/bulk`, { ids: selectedCharIds, ...payload });
      const map = Object.fromEntries((res.data.updated || []).map(c => [c.id, c]));
      setCharacters(prev => rankSort(prev.map(c => map[c.id] ? { ...c, ...map[c.id] } : c)));
      toast.success(`Applied to ${selectedCharIds.length} character${selectedCharIds.length > 1 ? 's' : ''}`);
    } catch { toast.error('Bulk update failed'); }
    finally { setBulkApplying(false); }
  };
  const allTags = Array.from(new Set(characters.flatMap(c => c.tags || []))).sort();
  const filteredCharacters = characters.filter(c => {
    const type = c.character_type || 'main';
    if (charTypeFilter !== 'all' && type !== charTypeFilter) return false;
    if (charTagFilter.length && !charTagFilter.every(t => (c.tags || []).includes(t))) return false;
    if (charSearch && !c.name.toLowerCase().includes(charSearch.toLowerCase())) return false;
    return true;
  });

  const fileInputRef = useRef(null);
  const [uploadCharId, setUploadCharId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, cRes, sRes] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/projects/${id}/characters`),
          api.get(`/projects/${id}/scenes`)
        ]);
        setProject(pRes.data);
        setCharacters(cRes.data);
        setScenes(sRes.data);
        // Load ElevenLabs voices for character voice selection
        try {
          const elRes = await api.get('/elevenlabs/voices');
          setElevenVoices(elRes.data.voices || []);
          setElevenKeyMissing(false);
        } catch (e) {
          setElevenKeyMissing(keyProblem(e, 'ElevenLabs'));
        }
        // Kling native voices (preset + cloned) for "lock a native video voice"
        try {
          const [pRes2, cRes2] = await Promise.all([api.get('/kling/preset-voices'), api.get('/kling/custom-voices')]);
          setKlingVoices([...(Array.isArray(cRes2.data) ? cRes2.data : []), ...(Array.isArray(pRes2.data) ? pRes2.data : [])]);
          setKlingKeyMissing(false);
        } catch (e) {
          setKlingKeyMissing(keyProblem(e, 'Kling'));
        }
      } catch (err) {
        toast.error('Failed to load project');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  // ── Cost roll-up + Share settings ──
  const fetchCostEstimate = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${id}/cost-estimate`);
      setCostEstimate(res.data);
    } catch { /* ignore */ }
  }, [id]);
  const fetchShareSettings = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${id}/share`);
      setShareSettings(res.data);
      setShareTitle(res.data?.share_title || '');
    } catch { /* ignore */ }
  }, [id]);
  const fetchFeedback = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${id}/feedback`);
      setFeedback(res.data);
    } catch { /* ignore */ }
  }, [id]);
  const [replyDrafts, setReplyDrafts] = useState({});
  const handleReplyFeedback = async (fid) => {
    const message = (replyDrafts[fid] || '').trim();
    if (!message) return;
    try {
      await api.post(`/projects/${id}/feedback/${fid}/reply`, { message });
      setReplyDrafts(d => ({ ...d, [fid]: '' }));
      toast.success('Reply sent');
      fetchFeedback();
    } catch { toast.error('Failed to send reply'); }
  };
  const handleResolveFeedback = async (fid, resolved) => {
    try {
      await api.post(`/projects/${id}/feedback/${fid}/resolve`, { resolved });
      fetchFeedback();
    } catch { toast.error('Failed to update'); }
  };
  useEffect(() => { fetchCostEstimate(); fetchShareSettings(); fetchFeedback(); }, [fetchCostEstimate, fetchShareSettings, fetchFeedback, scenes.length]);
  useEffect(() => { setBudgetInput(project?.cost_budget ? String(project.cost_budget) : ''); }, [project?.cost_budget]);

  const budget = Number(project?.cost_budget) || 0;
  const handleSaveShareTitle = async () => {
    try {
      const res = await api.post(`/projects/${id}/share`, { share_title: shareTitle });
      setShareSettings(res.data);
      toast.success('Title saved');
    } catch { toast.error('Failed to save title'); }
  };
  const handleSaveBudget = async () => {
    setSavingBudget(true);
    try {
      const val = budgetInput === '' ? 0 : parseFloat(budgetInput);
      await api.put(`/projects/${id}`, { cost_budget: isNaN(val) ? 0 : val });
      setProject(p => ({ ...p, cost_budget: isNaN(val) ? 0 : val }));
      setEditingBudget(false);
      toast.success(val > 0 ? 'Budget set' : 'Budget cleared');
    } catch { toast.error('Failed to save budget'); }
    finally { setSavingBudget(false); }
  };

  const costTotals = (() => {
    if (!costEstimate) return null;
    const sumVideos = (vids) => (vids || []).reduce((t, v) => t + (estimateVideoCost(v.provider, v.duration, v.resolution, rates) || 0), 0);
    const spent = sumVideos(costEstimate.spent?.videos) + estimateImagesCost(costEstimate.spent?.image_count || 0, rates);
    const projected = sumVideos(costEstimate.projected?.videos) + estimateImagesCost(costEstimate.projected?.image_count || 0, rates);
    return { spent, projected };
  })();

  const publicShareUrl = shareSettings?.token ? `${window.location.origin}/share/${shareSettings.token}` : '';

  const handleToggleShare = async (enabled) => {
    setSavingShare(true);
    try {
      const res = await api.post(`/projects/${id}/share`, { enabled });
      setShareSettings(res.data);
      toast.success(enabled ? 'Public sharing enabled' : 'Public sharing disabled');
    } catch { toast.error('Failed to update sharing'); }
    finally { setSavingShare(false); }
  };
  const handleSetSharePassword = async () => {
    setSavingShare(true);
    try {
      const res = await api.post(`/projects/${id}/share`, { password: sharePassword });
      setShareSettings(res.data);
      setSharePassword('');
      toast.success('Password set');
    } catch { toast.error('Failed to set password'); }
    finally { setSavingShare(false); }
  };
  const handleRemoveSharePassword = async () => {
    setSavingShare(true);
    try {
      const res = await api.post(`/projects/${id}/share`, { remove_password: true });
      setShareSettings(res.data);
      toast.success('Password removed');
    } catch { toast.error('Failed to remove password'); }
    finally { setSavingShare(false); }
  };

  // ── Character Actions ──
  const handleCreateChar = async (e) => {
    e.preventDefault();
    if (!charName.trim()) return;
    setCharCreating(true);
    try {
      const res = await api.post(`/projects/${id}/characters`, { name: charName, description: charDesc, character_type: charType, tags: charTags });
      const rank = { main: 0, background: 1, extra: 2 };
      setCharacters([res.data, ...characters].sort((a, b) => (rank[a.character_type || 'main'] ?? 0) - (rank[b.character_type || 'main'] ?? 0)));
      setShowCharCreate(false);
      setCharName('');
      setCharDesc('');
      setCharType('main');
      setCharTags([]);
      toast.success('Character created');
    } catch (err) {
      toast.error('Failed to create character');
    } finally {
      setCharCreating(false);
    }
  };

  const handleDeleteChar = async (charId) => {
    try {
      await api.delete(`/projects/${id}/characters/${charId}`);
      setCharacters(characters.filter(c => c.id !== charId));
      toast.success('Character deleted');
    } catch (err) {
      toast.error('Failed to delete character');
    }
  };

  const handleGenerateImage = async () => {
    if (!genPrompt.trim()) return;
    setGenLoading(true);
    try {
      await api.post(`/projects/${id}/characters/${genCharId}/generate-image`, { prompt: genPrompt }, { timeout: 15000 });
      // Poll for completion
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const statusRes = await api.get(`/projects/${id}/characters/${genCharId}/image-gen-status`, { params: { _t: Date.now() } });
          if (statusRes.data.status === 'complete' && statusRes.data.character) {
            setCharacters(prev => prev.map(c => c.id === genCharId ? statusRes.data.character : c));
            setGenCharId(null);
            setGenPrompt('');
            toast.success('Character image generated!');
            return;
          } else if (statusRes.data.status === 'failed') {
            toast.error(statusRes.data.error || 'Image generation failed');
            return;
          }
        } catch {}
      }
      toast.error('Image generation timed out');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image generation failed');
    } finally {
      setGenLoading(false);
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadCharId) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/projects/${id}/characters/${uploadCharId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCharacters(characters.map(c => c.id === uploadCharId ? res.data : c));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadCharId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Downloads ──
  const handleDownloadCharImage = (char) => {
    if (!char.image_storage_path) return;
    const url = getImageUrl(char.image_storage_path);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${char.name.replace(/[^a-zA-Z0-9]/g, '_')}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const [downloadingCastSheet, setDownloadingCastSheet] = useState(false);
  const [downloadingVideos, setDownloadingVideos] = useState(false);
  const [stitching, setStitching] = useState(false);
  const [mediaPicker, setMediaPicker] = useState(null);
  const handleUseMediaChar = async (item) => {
    if (!mediaPicker?.charId) return;
    const res = await api.post(`/projects/${id}/characters/${mediaPicker.charId}/use-media`, { path: item.path });
    setCharacters(prev => prev.map(c => c.id === mediaPicker.charId ? res.data : c));
    toast.success('Image applied');
  };
  const handleDownloadCastSheet = async () => {
    setDownloadingCastSheet(true);
    try {
      const res = await api.get(`/projects/${id}/cast-sheet`, { responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.name || 'cast'}_Cast_Sheet.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Cast sheet downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate cast sheet');
    } finally {
      setDownloadingCastSheet(false);
    }
  };

  // ── Scene Actions ──
  const handleSetVoice = async (charId, voiceId, voiceName) => {
    setSavingVoice(charId);
    try {
      const res = await api.put(`/projects/${id}/characters/${charId}`, { voice_id: voiceId, voice_name: voiceName });
      setCharacters(prev => prev.map(c => c.id === charId ? res.data : c));
      toast.success(`Voice set to ${voiceName || 'None'}`);
    } catch { toast.error('Failed to set voice'); }
    finally { setSavingVoice(null); }
  };

  const playVoicePreview = (voiceId, trialUrl) => {
    if (playingVoice === voiceId) {
      voiceAudioRef.current?.pause();
      setPlayingVoice(null);
      return;
    }
    if (voiceAudioRef.current) voiceAudioRef.current.pause();
    const audio = new Audio(trialUrl);
    audio.onended = () => setPlayingVoice(null);
    audio.play();
    voiceAudioRef.current = audio;
    setPlayingVoice(voiceId);
  };

  const handleCloneElevenVoice = async (e) => {
    const file = e.target.files?.[0];
    const charId = cloningVoiceCharId;
    if (!file || !charId) return;
    const char = characters.find(c => c.id === charId);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('voice_name', char?.name || 'Custom Voice');
    const tid = toast.loading('Cloning voice with ElevenLabs…');
    try {
      const res = await api.post('/elevenlabs/clone-voice', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
      });
      const v = res.data;
      setElevenVoices(prev => prev.find(x => x.voice_id === v.voice_id) ? prev : [{ ...v }, ...prev]);
      await handleSetVoice(charId, v.voice_id, v.name);
      toast.success('Voice cloned & assigned!', { id: tid });
    } catch (err) {
      toast.error(errMsg(err, 'Voice clone failed'), { id: tid });
    } finally {
      setCloningVoiceCharId(null);
      if (voiceCloneInputRef.current) voiceCloneInputRef.current.value = '';
    }
  };

  const handleSetNativeVoice = async (charId, voiceId, voiceName) => {
    setSavingVoice(charId);
    try {
      const res = await api.put(`/projects/${id}/characters/${charId}`, { kling_voice_id: voiceId, kling_voice_name: voiceName });
      setCharacters(prev => prev.map(c => c.id === charId ? res.data : c));
      toast.success(voiceName ? `Native voice locked: ${voiceName} (Kling only)` : 'Native voice cleared');
    } catch { toast.error('Failed to set native voice'); }
    finally { setSavingVoice(null); }
  };

  const handleCloneNativeVoice = async (e) => {
    const file = e.target.files?.[0];
    const charId = cloningNativeCharId;
    if (!file || !charId) return;
    const char = characters.find(c => c.id === charId);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('voice_name', (char?.name || 'Custom Voice').slice(0, 20));
    const tid = toast.loading('Cloning native voice with Kling…');
    try {
      const res = await api.post('/kling/clone-voice', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      const v = res.data;
      setKlingVoices(prev => prev.find(x => x.voice_id === v.voice_id) ? prev : [v, ...prev]);
      await handleSetNativeVoice(charId, v.voice_id, v.voice_name);
      toast.success('Native voice cloned & locked!', { id: tid });
    } catch (err) {
      toast.error(errMsg(err, 'Native voice clone failed'), { id: tid });
    } finally {
      setCloningNativeCharId(null);
      if (nativeCloneInputRef.current) nativeCloneInputRef.current.value = '';
    }
  };

  // ── Scene Actions ──
  const handleDownloadAllVideos = async () => {
    if (!scenes.some(s => s.video_path)) { toast.error('No generated videos to download yet'); return; }
    setDownloadingVideos(true);
    try {
      await api.post(`/projects/${id}/export-videos-zip`);
      refreshJobs();
      toast.success('Preparing your ZIP — track it in the Activity panel (bottom-right).');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start ZIP');
    } finally { setDownloadingVideos(false); }
  };

  const handleStitchFinalCut = async () => {
    if (!scenes.some(s => s.video_path)) { toast.error('No generated videos to stitch yet'); return; }
    setStitching(true);
    try {
      await api.post(`/projects/${id}/export-rough-cut`);
      refreshJobs();
      toast.success('Stitching your final cut in the background — track it in the Activity panel.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start stitch');
    } finally { setStitching(false); }
  };

  const handleReorderScenes = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = scenes.findIndex(s => s.id === active.id);
    const newIndex = scenes.findIndex(s => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(scenes, oldIndex, newIndex).map((s, i) => ({ ...s, scene_order: i }));
    setScenes(reordered); // optimistic
    try {
      await api.put(`/projects/${id}/scenes/reorder`, {
        scenes: reordered.map((s, i) => ({ id: s.id, scene_order: i }))
      });
      toast.success('Scene order updated');
    } catch {
      toast.error('Failed to save order');
      try { const r = await api.get(`/projects/${id}/scenes`); setScenes(r.data); } catch { /* ignore */ }
    }
  };

  const handleCreateNewScene = async () => {
    try {
      const res = await api.post(`/projects/${id}/scenes`, { title: 'Untitled Scene' });
      navigate(`/project/${id}/scenes/${res.data.id}`);
    } catch (err) {
      toast.error('Failed to create scene');
    }
  };

  const handleSceneFromDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setSceneDocBusy('Uploading document...');
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/projects/${id}/scenes/from-document`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      for (let i = 0; i < 100; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: job } = await api.get(`/projects/${id}/scene-identification-status`);
        if (job.progress) setSceneDocBusy(job.progress);
        if (job.status === 'complete' && job.simple) {
          const n = (job.created_scenes || []).length;
          toast.success(`${n} scene${n === 1 ? '' : 's'} created from the document`, { duration: 6000 });
          const sc = await api.get(`/projects/${id}/scenes`);
          setScenes(sc.data);
          return;
        }
        if (job.status === 'complete' && job.scene) {
          const n = (job.scene.new_character_ids || []).length;
          toast.success(`Scene "${job.scene.title}" created${n ? ` · ${n} new character${n > 1 ? 's' : ''} detected` : ''}`, { duration: 6000 });
          setCastReview(job.scene);
          return;
        }
        if (job.status === 'failed') { toast.error(job.error || 'Scene analysis failed'); break; }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not analyze document');
    } finally { setSceneDocBusy(''); }
  };

  const handleDeleteScene = async (sceneId) => {
    try {
      await api.delete(`/projects/${id}/scenes/${sceneId}`);
      setScenes(scenes.filter(s => s.id !== sceneId));
      toast.success('Scene deleted');
    } catch (err) {
      toast.error('Failed to delete scene');
    }
  };

  const handleDuplicateScene = async (sceneId) => {
    try {
      await api.post(`/projects/${id}/scenes/${sceneId}/duplicate`);
      const r = await api.get(`/projects/${id}/scenes`);
      setScenes(r.data);
      toast.success('Scene duplicated');
    } catch (err) {
      toast.error('Failed to duplicate scene');
    }
  };

  // ── Script Upload ──
  const handleScriptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScriptUploading(true);
    setScriptProgress('Uploading script...');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/projects/${id}/analyze-script`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      const jobId = res.data.job_id;
      if (!jobId) throw new Error('No job ID returned');

      // Poll for completion
      const poll = async () => {
        const maxAttempts = 120; // 10 min max
        for (let i = 0; i < maxAttempts; i++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const statusRes = await api.get(`/projects/${id}/script-status`);
            const job = statusRes.data;
            if (job.progress) setScriptProgress(job.progress);

            if (job.status === 'complete') {
              if (job.simple) {
                const [pRes, sRes] = await Promise.all([api.get(`/projects/${id}`), api.get(`/projects/${id}/scenes`)]);
                setProject(pRes.data);
                setScenes(sRes.data);
                toast.success(`${(job.created_scenes || []).length} scenes created from your document (prompts only)`);
                return;
              }
              // Refresh project and characters
              const [pRes, cRes] = await Promise.all([
                api.get(`/projects/${id}`),
                api.get(`/projects/${id}/characters`)
              ]);
              setProject(pRes.data);
              setCharacters(cRes.data);
              toast.success(job.progress || 'Script analysis complete!');
              if (job.match_count > 0) toast.info(`${job.match_count} character${job.match_count > 1 ? 's' : ''} may already exist in your workspace — review the suggestions in the Characters tab.`, { duration: 8000 });
              if (cRes.data.length > 0) setTab('characters');
              return;
            }
            if (job.status === 'failed') {
              toast.error(job.error || 'Script analysis failed');
              return;
            }
          } catch {
            // Polling error, keep trying
          }
        }
        toast.error('Script analysis timed out. Check back later.');
      };
      await poll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Script upload failed');
    } finally {
      setScriptUploading(false);
      setScriptProgress('');
      if (scriptInputRef.current) scriptInputRef.current.value = '';
    }
  };

  // ── Generate Scenes (use Production Mode) ──

  // ── Team Management ──
  const loadTeam = async () => {
    try {
      const res = await api.get(`/projects/${id}/team`);
      setTeamMembers(res.data.members);
      setPendingInvites(res.data.pending_invites);
    } catch (err) {
      toast.error('Failed to load team');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await api.post(`/projects/${id}/team/invite`, { email: inviteEmail });
      setPendingInvites(prev => [...prev, res.data]);
      setInviteEmail('');
      toast.success('Invite sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await api.delete(`/projects/${id}/team/${memberId}`);
      setTeamMembers(prev => prev.filter(m => m.id !== memberId));
      toast.success('Member removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await api.post(`/projects/${id}/team/invite/${inviteId}/resend`);
      toast.success('Invite email resent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };


  const handleCancelInvite = async (inviteId) => {
    try {
      await api.delete(`/projects/${id}/team/invite/${inviteId}`);
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
      toast.success('Invite cancelled');
    } catch (err) {
      toast.error('Failed to cancel invite');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060608]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="ff-pulse h-10 w-64 rounded mb-4" />
          <div className="ff-pulse h-6 w-96 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060608] overflow-x-hidden" data-testid="project-detail-page">
      {mediaPicker && (
        <MediaPicker open={!!mediaPicker} onClose={() => setMediaPicker(null)} projectId={id}
          mediaType={mediaPicker.mediaType} title={mediaPicker.title} onSelect={handleUseMediaChar} />
      )}
      {showCharImport && (
        <EntityPicker kind="character" projectId={id} initialTab="workspace" excludeIds={characters.map(c => c.id)} onClose={() => setShowCharImport(false)}
          title="Reuse a character from another project"
          onPick={(c) => { upsertCharacter(c); toast.success(`${c.name} added to this project`); }} />
      )}
      {linkChar && <LinkExistingPicker projectId={id} character={linkChar} onClose={() => setLinkChar(null)} onLinked={upsertCharacter} />}
      {castReview && <CastReviewModal projectId={id} scene={castReview} onDone={(sid) => { setCastReview(null); navigate(`/project/${id}/scenes/${sid}`); }} />}
      <input ref={sceneDocInputRef} type="file" accept=".txt,.md,.pdf,.rtf,.doc,.docx" className="hidden" onChange={handleSceneFromDocument} data-testid="scene-doc-input" />
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUploadImage} />
      <input ref={scriptInputRef} type="file" accept=".txt,.md,.pdf,.rtf,.doc,.docx" className="hidden" onChange={handleScriptUpload} />
      <input ref={voiceCloneInputRef} type="file" accept="audio/*,.mp3,.wav,.m4a" className="hidden" onChange={handleCloneElevenVoice} />
      <input ref={nativeCloneInputRef} type="file" accept="audio/*,.mp3,.wav,.m4a" className="hidden" onChange={handleCloneNativeVoice} data-testid="native-clone-input" />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <Breadcrumbs items={[{ label: project?.name || 'Project' }]} />
        {/* Header */}
        <div className="flex items-start gap-3 md:gap-4 mb-6 min-w-0">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors flex-shrink-0 mt-1"
          >
            <ArrowLeft className="w-5 h-5 text-[#8A8A93]" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {project?.name}
            </h1>
            <p className="text-[#8A8A93] text-sm mt-1 truncate">{project?.description || 'No description'}</p>
            <button type="button" data-testid="project-style-chip" onClick={openStyleDialog} title="Change the project's visual style"
              className="mt-2 inline-flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full border border-white/[0.08] bg-white/[0.03] hover:border-[#A78BFA]/50 hover:bg-[#A78BFA]/10 transition-colors group">
              <img src={styleThumb(project?.visual_style)} alt="" className="w-7 h-5 rounded-md object-cover" />
              <span className="text-[11px] font-medium text-[#C9C9CF] group-hover:text-white">{styleLabel(project?.visual_style)}</span>
              <Pencil className="w-3 h-3 text-[#8A8A93] group-hover:text-[#A78BFA]" />
            </button>
            {project?.style_mood && (
              <p className="text-[#00F0FF]/70 text-xs mt-2 line-clamp-2 leading-relaxed">
                {project.style_mood.length > 120 ? project.style_mood.slice(0, 120) + '...' : project.style_mood}
              </p>
            )}
          </div>
          <button
            data-testid="team-btn"
            onClick={() => { setShowTeamDialog(true); loadTeam(); }}
            className="ff-btn-secondary flex items-center gap-2 shrink-0 text-sm"
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Team</span>
          </button>
        </div>

        {/* Script Upload Banner */}
        {!project?.script_text && !isSimple && (
          <div
            data-testid="script-upload-banner"
            className="mb-6 md:mb-8 p-4 md:p-5 rounded-xl border border-dashed border-white/10 bg-[#121216] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-[#FF4500]/10 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 md:w-6 md:h-6 text-[#FF4500]" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm md:text-base">Upload a Script or Document</h3>
                <p className="text-[#8A8A93] text-xs md:text-sm mt-0.5">
                  AI will analyze it to extract characters, style, and mood
                </p>
              </div>
            </div>
            <button
              data-testid="upload-script-btn"
              onClick={() => scriptInputRef.current?.click()}
              disabled={scriptUploading}
              className="ff-btn-primary flex items-center gap-2 shrink-0"
            >
              {scriptUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {scriptUploading ? 'Analyzing...' : 'Upload Script'}
            </button>
          </div>
        )}
        {project?.script_text && !project?.style_mood && null}
        {scriptUploading && (
          <div className="mb-8 p-5 rounded-xl border border-[#FF4500]/20 bg-[#FF4500]/5 flex items-center gap-3" data-testid="script-progress-banner">
            <Loader2 className="w-5 h-5 text-[#FF4500] animate-spin shrink-0" />
            <span className="text-sm text-[#FF4500]">{scriptProgress || 'Analyzing script...'}</span>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6 md:mb-8">
            <TabsList className="bg-[#121216] border border-white/[0.06]">
              <TabsTrigger value="scenes" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-[#8A8A93] text-xs sm:text-sm" data-testid="tab-scenes">
                <Clapperboard className="w-4 h-4 mr-1 sm:mr-2" />
                Scenes ({scenes.length})
              </TabsTrigger>
              {!isSimple && <TabsTrigger value="characters" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-[#8A8A93] text-xs sm:text-sm" data-testid="tab-characters">
                <UserCircle className="w-4 h-4 mr-1 sm:mr-2" />
                Characters ({characters.length})
              </TabsTrigger>}
              {!isSimple && <TabsTrigger value="locations" className="data-[state=active]:bg-[#FF4500] data-[state=active]:text-white text-[#8A8A93] text-xs sm:text-sm" data-testid="tab-locations">
                <MapPin className="w-4 h-4 mr-1 sm:mr-2" />
                Locations
              </TabsTrigger>}
              {isSimple && (
                <button type="button" data-testid="simple-mode-badge" onClick={openStyleDialog} title="Simple mode — prompt + optional frames. Click to switch to Advanced."
                  className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 hover:bg-[#10B981]/20">
                  <Zap className="w-3 h-3" /> Simple mode
                </button>
              )}
            </TabsList>
            <button
              data-testid="copy-tab-link"
              onClick={() => copyShareLink(`/project/${id}?tab=${tab}`, `${tab === 'scenes' ? 'Scenes' : tab === 'locations' ? 'Locations' : 'Characters'} tab link`)}
              title="Copy a link that opens this tab"
              className="hidden sm:flex items-center gap-1.5 text-xs text-[#8A8A93] hover:text-[#00F0FF] transition-colors sm:ml-3 sm:mr-auto"
            >
              <Link2 className="w-3.5 h-3.5" /> Share tab
            </button>
            {tab === 'characters' ? (
              <div className="flex items-center gap-2">
                {!project?.script_text && (
                  <button
                    data-testid="upload-script-btn-header"
                    onClick={() => scriptInputRef.current?.click()}
                    disabled={scriptUploading}
                    className="ff-btn-secondary flex items-center gap-2 text-xs sm:text-sm"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">Upload Script</span>
                    <span className="sm:hidden">Script</span>
                  </button>
                )}
                <button
                  data-testid="import-character-btn"
                  onClick={() => setShowCharImport(true)}
                  className="ff-btn-secondary flex items-center gap-2 text-xs sm:text-sm"
                  title="Reuse a character from another project in this workspace"
                >
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">From another project</span>
                  <span className="sm:hidden">Reuse</span>
                </button>
                <button
                  data-testid="add-character-btn"
                  onClick={() => setShowCharCreate(true)}
                  className="ff-btn-primary flex items-center gap-2 text-xs sm:text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add Character</span>
                  <span className="sm:hidden">Add</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                {project?.script_text && (
                  <button
                    data-testid="enter-production-btn"
                    onClick={() => navigate(resumeScene ? `/project/${id}/production` : `/project/${id}/production`)}
                    className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-[#FF4500] text-white hover:bg-[#E03C00] transition-all font-semibold"
                    title={resumeScene ? `Continue: Scene ${scenes.indexOf(resumeScene) + 1} · ${WIZARD_LABELS[project.wizard_step] || 'Characters'}` : 'Start Production Mode'}
                  >
                    <Play className="w-4 h-4" />
                    {resumeScene ? <span>Continue <span className="hidden sm:inline font-normal opacity-80">· Scene {scenes.indexOf(resumeScene) + 1} · {WIZARD_LABELS[project.wizard_step] || 'Characters'}</span></span> : 'Production Mode'}
                  </button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="project-more-btn" className="relative flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-white/[0.03] text-[#c8c8d0] border border-white/[0.06] hover:text-white hover:bg-white/[0.06] transition-all font-medium">
                      <MoreVertical className="w-4 h-4" /> More
                      {feedback?.count > 0 && <span data-testid="feedback-count-badge" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF4500] text-white text-[10px] font-bold flex items-center justify-center">{feedback.count}</span>}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#1A1A20] border-white/10 text-white min-w-[220px]">
                    {scenes.length > 0 && (
                      <DropdownMenuItem data-testid="storyboard-btn" onClick={() => navigate(`/project/${id}/storyboard`)} className="gap-2 text-sm cursor-pointer"><LayoutList className="w-4 h-4 text-[#E879F9]" /> Storyboard</DropdownMenuItem>
                    )}
                    <DropdownMenuItem data-testid="change-style-btn" onClick={openStyleDialog} className="gap-2 text-sm cursor-pointer"><Wand2 className="w-4 h-4 text-[#A78BFA]" /> Visual style · {styleLabel(project?.visual_style)}</DropdownMenuItem>
                    <DropdownMenuItem data-testid="share-project-btn" onClick={() => { setShowShareDialog(true); fetchFeedback(); }} className="gap-2 text-sm cursor-pointer">
                      <Share2 className={`w-4 h-4 ${shareSettings?.enabled ? 'text-[#00F0FF]' : ''}`} /> {shareSettings?.enabled ? 'Sharing On' : 'Share with client'}
                      {feedback?.count > 0 && <span className="ml-auto text-[10px] px-1.5 rounded-full bg-[#FF4500] text-white font-bold">{feedback.count}</span>}
                    </DropdownMenuItem>
                    {scenes.some(s => s.video_path) && (
                      <>
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem data-testid="stitch-final-cut-btn" disabled={stitching} onClick={handleStitchFinalCut} className="gap-2 text-sm cursor-pointer">
                          {stitching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4 text-[#FF4500]" />} {stitching ? 'Starting…' : 'Stitch Final Cut'}
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid="download-all-videos-btn" disabled={downloadingVideos} onClick={handleDownloadAllVideos} className="gap-2 text-sm cursor-pointer">
                          {downloadingVideos ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 text-[#10B981]" />} {downloadingVideos ? 'Starting…' : 'Download All (ZIP)'}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <button
                  data-testid="add-scene-btn"
                  onClick={handleCreateNewScene}
                  className="ff-btn-primary flex items-center gap-2 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  New Scene
                </button>
                <button
                  data-testid="scene-from-doc-btn"
                  onClick={() => sceneDocInputRef.current?.click()}
                  disabled={!!sceneDocBusy}
                  title="Upload a document describing one scene — AI builds the cast, dialogue, setting and frame direction"
                  className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 hover:bg-[#00F0FF]/20 disabled:opacity-60 transition-all font-semibold"
                >
                  {sceneDocBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  <span className="hidden sm:inline">{sceneDocBusy ? sceneDocBusy : 'Scene from document'}</span>
                  <span className="sm:hidden">{sceneDocBusy ? '…' : 'From doc'}</span>
                </button>
              </div>
            )}
          </div>

          {/* Characters Tab */}
          <TabsContent value="characters">
            {characters.length === 0 ? (
              <GettingStarted testId="project-getting-started" title="Bring this project to life" subtitle="Follow the steps in order — each one unlocks the next."
                steps={[
                  { label: 'Import your script', hint: isSimple ? 'Split into scenes with a prompt each — no characters or locations.' : 'Characters, scenes, dialogue and settings are extracted automatically.', done: !!project?.script_text, action: { label: 'Upload Script', onClick: () => scriptInputRef.current?.click() } },
                  { label: 'Build your cast', hint: 'Generate a reference image per character, or add one manually / from another project.', done: false, action: { label: 'Add Character', onClick: () => setShowCharCreate(true) } },
                  { label: 'Enter Production Mode', hint: 'Walk each scene: cast → location → frames → audio → video.', done: false, action: project?.script_text ? { label: 'Production Mode', onClick: () => navigate(`/project/${id}/production`) } : null },
                ]} />
            ) : (
              <div>
                <CharacterMatchBanner projectId={id} characters={characters} onChange={upsertCharacter} onChooseOther={setLinkChar} />
                {/* Header actions */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="relative flex-1" data-testid="char-search-wrapper">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#55555C]" />
                    <input
                      data-testid="char-search-input"
                      type="text"
                      value={charSearch}
                      onChange={e => setCharSearch(e.target.value)}
                      className="ff-input w-full pl-10"
                      placeholder="Search characters by name..."
                    />
                    {charSearch && (
                      <button
                        onClick={() => setCharSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#55555C] hover:text-white transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {characters.some(c => c.image_storage_path) && (
                    <button
                      data-testid="download-cast-sheet"
                      onClick={handleDownloadCastSheet}
                      disabled={downloadingCastSheet}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 border border-[#10B981]/30 disabled:opacity-40 transition-all whitespace-nowrap"
                    >
                      {downloadingCastSheet ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      Cast Sheet PDF
                    </button>
                  )}
                  <button
                    data-testid="char-select-toggle"
                    onClick={() => { setSelectMode(m => !m); setSelectedCharIds([]); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border transition-all whitespace-nowrap ${selectMode ? 'bg-[#A78BFA]/20 text-[#A78BFA] border-[#A78BFA]/40' : 'bg-white/[0.03] text-[#8A8A93] border-white/[0.06] hover:text-white'}`}
                  >
                    <CheckSquare className="w-4 h-4" /> {selectMode ? 'Done' : 'Select'}
                  </button>
                </div>
                {/* Type + Tag filters */}
                <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="char-filters">
                  <div className="flex gap-1.5">
                    {['all', 'main', 'background', 'extra'].map(t => (
                      <button key={t} data-testid={`char-filter-type-${t}`} onClick={() => setCharTypeFilter(t)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all border ${charTypeFilter === t ? 'bg-[#FF4500]/15 text-[#FF4500] border-[#FF4500]/30' : 'bg-white/[0.03] text-[#8A8A93] border-white/[0.06] hover:text-white'}`}>
                        {t === 'all' ? 'All types' : t}
                      </button>
                    ))}
                  </div>
                  {allTags.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] text-[#55555C] ml-1 flex items-center gap-1"><Tag className="w-3 h-3" /> Tags:</span>
                      {allTags.map(t => {
                        const on = charTagFilter.includes(t);
                        return (
                          <button key={t} data-testid={`char-filter-tag-${t}`}
                            onClick={() => setCharTagFilter(prev => on ? prev.filter(x => x !== t) : [...prev, t])}
                            className={`px-2 py-1 rounded-md text-[11px] transition-all border ${on ? 'bg-[#A78BFA]/20 text-[#A78BFA] border-[#A78BFA]/40' : 'bg-white/[0.03] text-[#8A8A93] border-white/[0.06] hover:text-white'}`}>
                            {t}
                          </button>
                        );
                      })}
                      {(charTypeFilter !== 'all' || charTagFilter.length > 0) && (
                        <button data-testid="char-filter-clear" onClick={() => { setCharTypeFilter('all'); setCharTagFilter([]); }}
                          className="px-2 py-1 rounded-md text-[11px] text-[#55555C] hover:text-white flex items-center gap-1">
                          <X className="w-3 h-3" /> Clear
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {selectMode && (
                  <div className="flex flex-wrap items-center gap-3 mb-5 p-3 rounded-xl bg-[#A78BFA]/10 border border-[#A78BFA]/25" data-testid="bulk-action-bar">
                    <span className="text-xs font-semibold text-[#A78BFA]" data-testid="bulk-selected-count">{selectedCharIds.length} selected</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#8A8A93] uppercase tracking-wider">Set type</span>
                      {['main', 'background', 'extra'].map(t => (
                        <button key={t} data-testid={`bulk-set-type-${t}`} disabled={!selectedCharIds.length || bulkApplying}
                          onClick={() => applyBulk({ character_type: t })}
                          className="px-2.5 py-1 rounded-md text-[11px] font-semibold capitalize bg-white/[0.05] text-white hover:bg-white/[0.12] border border-white/10 disabled:opacity-40 transition-colors">
                          {t}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <input data-testid="bulk-tag-input" value={bulkTag} onChange={e => setBulkTag(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && bulkTag.trim() && selectedCharIds.length) { applyBulk({ add_tags: [bulkTag.trim()] }); setBulkTag(''); } }}
                        className="ff-input text-xs py-1.5 w-40" placeholder="Add a tag to all..." />
                      <button data-testid="bulk-add-tag" disabled={!bulkTag.trim() || !selectedCharIds.length || bulkApplying}
                        onClick={() => { applyBulk({ add_tags: [bulkTag.trim()] }); setBulkTag(''); }}
                        className="px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-[#A78BFA]/20 text-[#A78BFA] hover:bg-[#A78BFA]/30 border border-[#A78BFA]/40 disabled:opacity-40 flex items-center gap-1 transition-colors">
                        <Tag className="w-3 h-3" /> Add tag
                      </button>
                    </div>
                    <button data-testid="bulk-select-all" onClick={() => setSelectedCharIds(filteredCharacters.map(c => c.id))}
                      className="text-[11px] text-[#8A8A93] hover:text-white transition-colors">Select all ({filteredCharacters.length})</button>
                    {selectedCharIds.length > 0 && (
                      <button data-testid="bulk-clear" onClick={() => setSelectedCharIds([])}
                        className="text-[11px] text-[#55555C] hover:text-white flex items-center gap-1 transition-colors"><X className="w-3 h-3" /> Clear</button>
                    )}
                  </div>
                )}
                {filteredCharacters.length === 0 ? (
                  <div className="text-center py-12">
                    <Search className="w-10 h-10 text-[#55555C] mx-auto mb-3" />
                    <p className="text-[#8A8A93] text-sm">No characters match your filters</p>
                  </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {filteredCharacters.map(char => (
                  <div key={char.id} className={`ff-card group relative ${selectMode && selectedCharIds.includes(char.id) ? 'ring-2 ring-[#A78BFA]' : ''}`} data-testid={`char-card-${char.id}`}>
                    {selectMode && (
                      <button data-testid={`char-select-${char.id}`} onClick={() => toggleSelect(char.id)}
                        className="absolute top-2 left-2 z-20 p-1 rounded-md bg-black/60 hover:bg-black/80 transition-colors">
                        {selectedCharIds.includes(char.id) ? <CheckSquare className="w-5 h-5 text-[#A78BFA]" /> : <Square className="w-5 h-5 text-white" />}
                      </button>
                    )}
                    {/* Character Image */}
                    <div className="aspect-square bg-[#0a0a0e] relative overflow-hidden">
                      {char.image_storage_path ? (
                        <img
                          src={getImageUrl(char.image_storage_path)}
                          alt={char.name}
                          className="w-full h-full object-cover"
                          data-testid={`char-image-${char.id}`}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-[#55555C]">
                          <ImageIcon className="w-12 h-12 mb-2" />
                          <span className="text-xs">No image</span>
                        </div>
                      )}
                      {/* Overlay Actions */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        <button
                          data-testid={`gen-image-btn-${char.id}`}
                          onClick={() => { setGenCharId(char.id); setGenPrompt(char.description || ''); }}
                          className="p-2.5 rounded-lg bg-[#FF4500] hover:bg-[#FF5722] text-white transition-colors"
                          title="Generate Image"
                        >
                          <Sparkles className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`upload-image-btn-${char.id}`}
                          onClick={() => { setUploadCharId(char.id); fileInputRef.current?.click(); }}
                          className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                          title="Upload Image"
                        >
                          <Upload className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`use-existing-char-${char.id}`}
                          onClick={() => setMediaPicker({ charId: char.id, mediaType: 'image', title: `Use Existing Image — ${char.name}` })}
                          className="p-2.5 rounded-lg bg-[#E879F9]/20 hover:bg-[#E879F9]/30 text-[#E879F9] transition-colors"
                          title="Use existing / previous image"
                        >
                          <ImageIcon className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`link-existing-char-${char.id}`}
                          onClick={() => setLinkChar(char)}
                          className="p-2.5 rounded-lg bg-[#00F0FF]/15 hover:bg-[#00F0FF]/25 text-[#00F0FF] transition-colors"
                          title="This is an existing character from another project"
                        >
                          <Users className="w-4 h-4" />
                        </button>
                        {char.image_storage_path && (
                          <button
                            data-testid={`download-char-img-${char.id}`}
                            onClick={() => handleDownloadCharImage(char)}
                            className="p-2.5 rounded-lg bg-[#10B981]/20 hover:bg-[#10B981]/30 text-[#10B981] transition-colors"
                            title="Download Image"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={`delete-char-btn-${char.id}`}
                          onClick={() => handleDeleteChar(char.id)}
                          className="p-2.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-semibold text-white">{char.name}</h4>
                        <TypeBadge type={char.character_type} />
                        <SyncBadge kind="character" entity={char} onDetached={upsertCharacter} />
                      </div>
                      <p className="text-xs text-[#8A8A93] line-clamp-2">{char.description || 'No description'}</p>
                      {(char.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2" data-testid={`char-card-tags-${char.id}`}>
                          {(char.tags || []).map(t => (
                            <span key={t} className="px-1.5 py-0.5 rounded bg-[#A78BFA]/12 text-[#A78BFA] text-[10px]">{t}</span>
                          ))}
                        </div>
                      )}
                      <button
                        data-testid={`edit-char-btn-${char.id}`}
                        onClick={() => {
                          setViewCharId(char.id);
                          setEditDialogName(char.name);
                          setEditDialogDesc(char.description || '');
                          setEditDialogType(char.character_type || 'main');
                          setEditDialogTags(char.tags || []);
                        }}
                        className="mt-1.5 flex items-center gap-1 text-[10px] font-medium text-[#FF4500] hover:text-[#FF5722] transition-colors"
                      >
                        <Pencil className="w-3 h-3" /> Edit details
                      </button>
                      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex flex-wrap gap-1" data-testid={`char-status-chips-${char.id}`}>
                          {[
                            { on: !!char.voice_id, l: 'ElevenLabs voice', c: '#A78BFA' },
                            { on: !!char.kling_voice_id, l: 'Native voice', c: '#E879F9' },
                            { on: !!(char.voice_description || '').trim(), l: 'Voice profile', c: '#10B981' },
                          ].map(ch => (
                            <span key={ch.l} className="text-[9px] px-1.5 py-0.5 rounded-full border flex items-center gap-1"
                              style={ch.on ? { color: ch.c, borderColor: `${ch.c}44`, background: `${ch.c}14` } : { color: '#55555C', borderColor: 'rgba(255,255,255,0.08)' }}>
                              {ch.on ? <Check className="w-2.5 h-2.5" /> : <span className="w-2.5 text-center">–</span>} {ch.l}
                            </span>
                          ))}
                        </div>
                        <button data-testid={`char-expand-${char.id}`} onClick={() => setExpandedChars(prev => { const n = new Set(prev); n.has(char.id) ? n.delete(char.id) : n.add(char.id); return n; })}
                          className="text-[10px] text-[#8A8A93] hover:text-white flex items-center gap-1">
                          {expandedChars.has(char.id) ? 'Hide voices' : 'Voices & profile'} <ChevronDown className={`w-3 h-3 transition-transform ${expandedChars.has(char.id) ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      {expandedChars.has(char.id) && (<div data-testid={`char-details-${char.id}`}>
                      {/* Voice Selector (ElevenLabs) */}
                      <div className="mt-3 pt-3 border-t border-white/[0.06]">
                        <label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#A78BFA] mb-2 flex items-center gap-1">
                          <Volume2 className="w-3 h-3" /> Voice
                        </label>
                        {elevenKeyMissing ? (
                          <p data-testid={`voice-helper-${char.id}`} className="text-[10px] text-[#8A8A93] leading-relaxed">
                            {typeof elevenKeyMissing === 'string' ? elevenKeyMissing : 'Add an ElevenLabs API key in Team Settings to assign character voices.'}
                            {' '}<button onClick={() => navigate('/settings')} className="text-[#FF4500] hover:underline">Open Team Settings →</button>
                          </p>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Select
                              value={char.voice_id || 'none'}
                              disabled={savingVoice === char.id}
                              onValueChange={(val) => {
                                if (val === 'none') { handleSetVoice(char.id, '', ''); }
                                else {
                                  const v = elevenVoices.find(x => x.voice_id === val);
                                  handleSetVoice(char.id, val, v?.name || '');
                                }
                              }}
                            >
                              <SelectTrigger data-testid={`voice-select-${char.id}`} className="flex-1 h-9 text-xs bg-[#121216] border-white/10">
                                <SelectValue placeholder="No voice selected" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#1A1A20] border-white/10 max-h-72">
                                <SelectItem value="none" className="text-xs text-[#8A8A93]">No voice selected</SelectItem>
                                {elevenVoices.map(v => (
                                  <SelectItem key={v.voice_id} value={v.voice_id} data-testid={`voice-option-${v.voice_id}`} className="text-xs">
                                    {v.name}
                                    {v.labels?.accent ? <span className="text-[#55555C] ml-1">· {v.labels.accent}</span> : null}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <button
                              data-testid={`voice-preview-${char.id}`}
                              disabled={!char.voice_id}
                              onClick={() => {
                                const v = elevenVoices.find(x => x.voice_id === char.voice_id);
                                if (v?.preview_url) playVoicePreview(char.voice_id, v.preview_url);
                                else toast.error('No preview available for this voice');
                              }}
                              title="Play voice preview"
                              className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              {playingVoice === char.voice_id ? <div className="w-3 h-3 rounded-sm bg-[#A78BFA]" /> : <Play className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        )}
                        {!elevenKeyMissing && (
                          <button
                            data-testid={`clone-voice-${char.id}`}
                            onClick={() => { setCloningVoiceCharId(char.id); voiceCloneInputRef.current?.click(); }}
                            className="mt-2 text-[10px] text-[#00F0FF] hover:text-[#33F3FF] flex items-center gap-1 transition-colors"
                          >
                            <Upload className="w-3 h-3" /> Clone a voice from audio…
                          </button>
                        )}
                      </div>
                      <VoiceDescriptionEditor char={char} projectId={id} onUpdated={(c) => setCharacters(prev => prev.map(x => x.id === c.id ? c : x))} />
                      <NativeVoicePicker char={char} voices={klingVoices} keyMissing={klingKeyMissing} saving={savingVoice === char.id} playing={playingVoice}
                        onSet={(vid, vname) => handleSetNativeVoice(char.id, vid, vname)}
                        onPreview={(vid, url) => url ? playVoicePreview(vid, url) : toast.error('No preview available for this voice')}
                        onCloneClick={() => { setCloningNativeCharId(char.id); nativeCloneInputRef.current?.click(); }} />
                      </div>)}
                    </div>
                  </div>
                ))}
              </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Scenes Tab */}
          <TabsContent value="locations">
            <ProjectLocations projectId={id} />
          </TabsContent>

          <TabsContent value="scenes">
            {scenes.length === 0 ? (
              <div className="text-center py-20">
                <Clapperboard className="w-16 h-16 text-[#55555C] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#8A8A93] mb-2">No scenes yet</h3>
                <p className="text-[#55555C] mb-6">Create scenes manually or enter Production Mode to let AI guide you scene by scene</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  {project?.script_text && (
                    <button
                      data-testid="empty-enter-production"
                      onClick={() => navigate(`/project/${id}/production`)}
                      className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-[#FF4500]/15 text-[#FF4500] hover:bg-[#FF4500]/25 border border-[#FF4500]/30 transition-all font-medium"
                    >
                      <Play className="w-4 h-4" />
                      Enter Production Mode
                    </button>
                  )}
                  <button onClick={handleCreateNewScene} className="ff-btn-primary flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    New Scene
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {costTotals && (
                  <div data-testid="project-cost-rollup" className={`p-4 rounded-xl border ${budget > 0 && costTotals.projected > budget ? 'bg-red-500/[0.06] border-red-500/30' : 'bg-[#10B981]/[0.06] border-[#10B981]/20'}`}>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <div className={`flex items-center gap-2 ${budget > 0 && costTotals.projected > budget ? 'text-red-400' : 'text-[#10B981]'}`}>
                        <DollarSign className="w-4 h-4" />
                        <span className="text-xs tracking-[0.15em] uppercase font-semibold">Estimated Cost</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-[#8A8A93] block">Spent so far</span>
                        <span data-testid="cost-spent" className="text-lg font-bold text-white">{formatCost(costTotals.spent)}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-[#8A8A93] block">Projected (frames + 1 video/scene)</span>
                        <span data-testid="cost-projected" className="text-lg font-bold text-white">{formatCost(costTotals.projected)}</span>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        {editingBudget ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-[#8A8A93]">$</span>
                            <input data-testid="budget-input" type="number" step="1" min="0" value={budgetInput} onChange={e => setBudgetInput(e.target.value)}
                              placeholder="Budget" autoFocus className="ff-input w-24 px-2 py-1 text-sm rounded-lg" />
                            <button data-testid="save-budget-btn" onClick={handleSaveBudget} disabled={savingBudget}
                              className="p-1.5 rounded-lg bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25"><Check className="w-3.5 h-3.5" /></button>
                            <button onClick={() => { setEditingBudget(false); setBudgetInput(budget ? String(budget) : ''); }}
                              className="p-1.5 rounded-lg text-[#8A8A93] hover:text-white"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        ) : (
                          <button data-testid="set-budget-btn" onClick={() => setEditingBudget(true)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-[#8A8A93] hover:text-white transition-colors">
                            <Pencil className="w-3 h-3" /> {budget > 0 ? `Budget: ${formatCost(budget)}` : 'Set budget'}
                          </button>
                        )}
                      </div>
                    </div>
                    {budget > 0 && costTotals.projected > budget && (
                      <div data-testid="cost-alert" className="flex items-center gap-2 mt-3 pt-3 border-t border-red-500/20 text-xs text-red-400">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        Projected spend ({formatCost(costTotals.projected)}) is over your {formatCost(budget)} budget by {formatCost(costTotals.projected - budget)}.
                      </div>
                    )}
                    {budget === 0 && (
                      <span className="text-[10px] text-[#55555C] block mt-2">Approximate estimate based on provider pricing. Edit rates in Team Settings.</span>
                    )}
                  </div>
                )}
                <SceneMatrix projectId={id} scenes={scenes} simple={isSimple} />
                <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
                  <p className="text-xs text-[#8A8A93] flex items-center gap-1.5">
                    <GripVertical className="w-3.5 h-3.5" /> {sceneFilter === 'all' ? 'Drag scenes to set their order — this order is used for ZIP downloads and the Final Cut.' : 'Clear the filter to reorder scenes.'}
                  </p>
                  <div className="flex gap-1 p-0.5 rounded-lg bg-white/[0.04] border border-white/[0.06]" data-testid="scene-status-filter">
                    {SCENE_FILTERS.map(f => {
                      const n = f.id === 'all' ? scenes.length : scenes.filter(sc => sceneStatus(sc) === f.id).length;
                      return (
                        <button key={f.id} data-testid={`scene-filter-${f.id}`} onClick={() => setSceneFilter(f.id)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-medium flex items-center gap-1.5 transition-all ${sceneFilter === f.id ? 'bg-[#FF4500] text-white' : 'text-[#8A8A93] hover:text-white'}`}>
                          {f.color && <span className="w-1.5 h-1.5 rounded-full" style={{ background: sceneFilter === f.id ? '#fff' : f.color }} />}
                          {f.label} <span className={sceneFilter === f.id ? 'text-white/70' : 'text-[#55555C]'}>{n}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {visibleScenes.length === 0 && (
                  <p data-testid="scene-filter-empty" className="text-xs text-[#55555C] text-center py-8">No scenes match “{SCENE_FILTERS.find(f => f.id === sceneFilter).label}”.</p>
                )}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={sceneFilter === 'all' ? handleReorderScenes : undefined}>
                  <SortableContext items={visibleScenes.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-3">
                      {visibleScenes.map((scene) => (
                        <SortableScene
                          key={scene.id}
                          scene={scene}
                          sceneNumber={scenes.indexOf(scene) + 1}
                          projectId={id}
                          characters={characters}
                          onDelete={handleDeleteScene}
                          onUpdate={(sc) => setScenes(prev => prev.map(x => x.id === sc.id ? sc : x))}
                          onDuplicate={handleDuplicateScene}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Character Dialog */}
      <Dialog open={showCharCreate} onOpenChange={setShowCharCreate}>
        <DialogContent className="bg-[#121216] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>New Character</DialogTitle>
            <DialogDescription className="text-[#8A8A93]">Define a character to use in your scenes</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateChar} className="space-y-4 mt-2">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8A8A93] mb-2 block">Character Name</label>
              <input data-testid="char-name-input" value={charName} onChange={e => setCharName(e.target.value)} className="ff-input w-full" placeholder="e.g. Captain Nova" required autoFocus />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8A8A93] mb-2 block">Description</label>
              <textarea data-testid="char-desc-input" value={charDesc} onChange={e => setCharDesc(e.target.value)} className="ff-input w-full h-28 resize-none" placeholder="Describe the character's appearance, clothing, distinguishing features..." />
            </div>
            <TypeTagEditor type={charType} onType={setCharType} tags={charTags} onTags={setCharTags} allTags={allTags} />
            <DialogFooter>
              <button type="button" onClick={() => setShowCharCreate(false)} className="ff-btn-secondary">Cancel</button>
              <button data-testid="char-submit-btn" type="submit" disabled={charCreating} className="ff-btn-primary flex items-center gap-2">
                {charCreating ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Plus className="w-4 h-4" />}
                Create
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Generate Image Dialog */}
      <Dialog open={!!genCharId} onOpenChange={() => { setGenCharId(null); setGenPrompt(''); }}>
        <DialogContent className="bg-[#121216] border-white/10 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Generate Character Image
            </DialogTitle>
            <DialogDescription className="text-[#8A8A93]">
              Describe how this character should look. The AI will generate a reference portrait.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8A8A93] mb-2 block">Image Prompt</label>
              <textarea
                data-testid="gen-image-prompt"
                value={genPrompt}
                onChange={e => setGenPrompt(e.target.value)}
                className="ff-input w-full h-32 resize-none"
                placeholder="e.g. A rugged space captain in a dark leather jacket, short cropped hair, scar across left cheek, mid-30s, determined expression..."
              />
            </div>
            {genLoading && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-[#FF4500]/5 border border-[#FF4500]/20">
                <div className="w-5 h-5 border-2 border-[#FF4500]/30 border-t-[#FF4500] rounded-full animate-spin" />
                <span className="text-sm text-[#FF4500]">Generating image with Nano Banana...</span>
              </div>
            )}
            <DialogFooter>
              <button onClick={() => { setGenCharId(null); setGenPrompt(''); }} className="ff-btn-secondary">Cancel</button>
              <button
                data-testid="gen-image-submit"
                onClick={handleGenerateImage}
                disabled={genLoading || !genPrompt.trim()}
                className="ff-btn-primary flex items-center gap-2"
              >
                {genLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate
              </button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Management Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent className="bg-[#121216] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Team
            </DialogTitle>
            <DialogDescription className="text-[#8A8A93]">
              Invite others to collaborate on this project
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            {/* Invite Form */}
            <form onSubmit={handleInvite} className="flex gap-2">
              <input
                data-testid="invite-email-input"
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                className="ff-input flex-1"
                placeholder="Email address"
                required
              />
              <button
                data-testid="send-invite-btn"
                type="submit"
                disabled={inviting}
                className="ff-btn-primary flex items-center gap-2 shrink-0 text-sm"
              >
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Invite
              </button>
            </form>

            {/* Members List */}
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8A8A93] mb-3">Members</p>
              <div className="space-y-2">
                {teamMembers.map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 rounded-lg bg-[#0a0a0e] border border-white/[0.04]" data-testid={`team-member-${member.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#FF4500]/15 flex items-center justify-center">
                        {member.role === 'owner' ? <Crown className="w-4 h-4 text-[#FF4500]" /> : <UserCircle className="w-4 h-4 text-[#8A8A93]" />}
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{member.name}</p>
                        <p className="text-xs text-[#55555C]">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-[#55555C]">{member.role}</span>
                      {member.role === 'member' && project?.user_id !== member.id && (
                        <button
                          data-testid={`remove-member-${member.id}`}
                          onClick={() => handleRemoveMember(member.id)}
                          className="p-1 rounded hover:bg-red-500/10 text-[#55555C] hover:text-red-400 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8A8A93] mb-3">Pending Invites</p>
                <div className="space-y-2">
                  {pendingInvites.map(invite => (
                    <div key={invite.id} className="flex items-center justify-between p-3 rounded-lg bg-[#0a0a0e] border border-dashed border-white/[0.08]" data-testid={`pending-invite-${invite.id}`}>
                      <div>
                        <p className="text-sm text-white">{invite.invitee_name || invite.invitee_email}</p>
                        <p className="text-xs text-[#55555C]">{invite.invitee_email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          data-testid={`resend-invite-${invite.id}`}
                          onClick={() => handleResendInvite(invite.id)}
                          className="text-xs text-[#FF4500] hover:text-[#FF6a33] transition-colors font-medium"
                        >
                          Resend
                        </button>
                        <button
                          data-testid={`cancel-invite-${invite.id}`}
                          onClick={() => handleCancelInvite(invite.id)}
                          className="text-xs text-[#8A8A93] hover:text-red-400 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Character Detail Dialog */}
      <Dialog open={!!viewCharId} onOpenChange={() => setViewCharId(null)}>
        <DialogContent className="bg-[#121216] border-white/10 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Edit Character
            </DialogTitle>
            <DialogDescription className="text-[#8A8A93]">
              Update name and description
            </DialogDescription>
          </DialogHeader>
          {viewChar && (
            <div className="space-y-4 mt-2">
              {viewChar.image_storage_path && (
                <div className="aspect-video bg-[#0a0a0e] rounded-lg overflow-hidden">
                  <img
                    src={getImageUrl(viewChar.image_storage_path)}
                    alt={viewChar.name}
                    className="w-full h-full object-contain"
                    data-testid="char-detail-image"
                  />
                </div>
              )}
              <div>
                <label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8A8A93] mb-1.5 block">Name</label>
                <input
                  data-testid="char-edit-name"
                  value={editDialogName}
                  onChange={e => setEditDialogName(e.target.value)}
                  className="ff-input w-full text-sm"
                  placeholder="Character name"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8A8A93] mb-1.5 block">Description</label>
                <textarea
                  data-testid="char-edit-desc"
                  value={editDialogDesc}
                  onChange={e => setEditDialogDesc(e.target.value)}
                  className="ff-input w-full h-40 resize-y text-sm leading-relaxed"
                  placeholder="Physical appearance, personality, wardrobe..."
                />
              </div>
              <TypeTagEditor type={editDialogType} onType={setEditDialogType} tags={editDialogTags} onTags={setEditDialogTags} allTags={allTags} />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setViewCharId(null)}
                  className="px-4 py-2 rounded-lg text-sm text-[#8A8A93] hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="char-edit-save"
                  disabled={savingChar}
                  onClick={async () => {
                    setSavingChar(true);
                    try {
                      const updates = {};
                      if (editDialogName.trim() && editDialogName !== viewChar.name) updates.name = editDialogName.trim();
                      if (editDialogDesc !== viewChar.description) updates.description = editDialogDesc;
                      if (editDialogType !== (viewChar.character_type || 'main')) updates.character_type = editDialogType;
                      if (JSON.stringify(editDialogTags) !== JSON.stringify(viewChar.tags || [])) updates.tags = editDialogTags;
                      if (Object.keys(updates).length > 0) {
                        await api.put(`/projects/${id}/characters/${viewChar.id}`, updates);
                        const rank = { main: 0, background: 1, extra: 2 };
                        setCharacters(prev => prev.map(c => c.id === viewChar.id ? { ...c, ...updates } : c)
                          .sort((a, b) => (rank[a.character_type || 'main'] ?? 0) - (rank[b.character_type || 'main'] ?? 0)));
                        toast.success(`${updates.name || viewChar.name} saved!`);
                      }
                      setViewCharId(null);
                    } catch { toast.error('Failed to save'); }
                    finally { setSavingChar(false); }
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#10B981] hover:bg-[#10B981]/80 text-white flex items-center gap-2 transition-colors disabled:opacity-40"
                >
                  {savingChar ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Visual Style Dialog */}
      <Dialog open={showStyleDialog} onOpenChange={setShowStyleDialog}>
        <DialogContent className="bg-[#0d0d11] border-white/10 text-white max-w-3xl" data-testid="style-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-[#A78BFA]" /> Project style & mode</DialogTitle>
            <DialogDescription className="text-[#8A8A93]">Applies to everything you generate from now on — frames, character art, locations and video prompts. Already-generated media stays as is until you regenerate it.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-5">
            <CreationModeSelector value={modeDraft} onChange={setModeDraft} testIdPrefix="pmode" />
            <StyleSelector value={styleDraft} onChange={setStyleDraft} testIdPrefix="pstyle" />
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setShowStyleDialog(false)} className="ff-btn-secondary text-sm">Cancel</button>
            <button type="button" data-testid="save-style-btn" onClick={handleSaveStyle} disabled={savingStyle || (styleDraft === (project?.visual_style || 'photorealistic') && modeDraft === (project?.creation_mode || 'advanced'))} className="ff-btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
              {savingStyle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-[#0d0d11] border-white/10 text-white max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[#00F0FF]" /> Share with a client</DialogTitle>
            <DialogDescription className="text-[#8A8A93]">Anyone with the link can watch the rough cut — no login needed. Cost info stays private.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#121216] border border-white/[0.06]">
              <div>
                <p className="text-sm text-white font-medium">Public sharing</p>
                <p className="text-xs text-[#8A8A93]">{shareSettings?.enabled ? 'This project is viewable via link' : 'Turn on to create a link'}</p>
              </div>
              <button data-testid="share-toggle" onClick={() => handleToggleShare(!shareSettings?.enabled)} disabled={savingShare}
                className={`relative w-11 h-6 rounded-full transition-all ${shareSettings?.enabled ? 'bg-[#00F0FF]' : 'bg-white/10'}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${shareSettings?.enabled ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>

            {shareSettings?.enabled && (
              <>
                <div>
                  <label className="text-xs text-[#8A8A93] block mb-1">Custom title (shown to clients)</label>
                  <div className="flex gap-2">
                    <input data-testid="share-title-input" value={shareTitle} onChange={e => setShareTitle(e.target.value)}
                      placeholder={project?.title || 'Project title'}
                      className="flex-1 px-3 py-2 rounded-lg bg-[#121216] border border-white/10 text-xs text-white" />
                    <button data-testid="save-share-title-btn" onClick={handleSaveShareTitle}
                      className="px-3 py-2 rounded-lg bg-white/[0.05] text-white hover:bg-white/10 border border-white/10 text-xs font-medium">Save</button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#8A8A93] block mb-1">Public link</label>
                  <div className="flex gap-2">
                    <input data-testid="share-link-input" readOnly value={publicShareUrl}
                      className="flex-1 px-3 py-2 rounded-lg bg-[#121216] border border-white/10 text-xs text-white truncate" />
                    <button data-testid="copy-share-link-btn" onClick={() => copyShareLink(`/share/${shareSettings.token}`, 'Share link')}
                      className="px-3 py-2 rounded-lg bg-[#00F0FF]/15 text-[#00F0FF] hover:bg-[#00F0FF]/25 border border-[#00F0FF]/30 flex items-center gap-1.5 text-xs font-medium">
                      <Link2 className="w-3.5 h-3.5" /> Copy
                    </button>
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-[#121216] border border-white/[0.06]">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-3.5 h-3.5 text-[#A78BFA]" />
                    <span className="text-sm text-white font-medium">Password protection</span>
                    {shareSettings?.has_password && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981]">On</span>}
                  </div>
                  <div className="flex gap-2">
                    <input data-testid="share-password-input" type="text" value={sharePassword} onChange={e => setSharePassword(e.target.value)}
                      placeholder={shareSettings?.has_password ? 'Set a new password' : 'Optional password'}
                      className="flex-1 px-3 py-2 rounded-lg bg-[#0d0d11] border border-white/10 text-xs text-white" />
                    <button data-testid="set-share-password-btn" onClick={handleSetSharePassword} disabled={savingShare || !sharePassword}
                      className="px-3 py-2 rounded-lg bg-[#A78BFA]/15 text-[#A78BFA] hover:bg-[#A78BFA]/25 border border-[#A78BFA]/30 text-xs font-medium disabled:opacity-40">Set</button>
                    {shareSettings?.has_password && (
                      <button data-testid="remove-share-password-btn" onClick={handleRemoveSharePassword} disabled={savingShare}
                        className="px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 text-xs font-medium">Remove</button>
                    )}
                  </div>
                </div>
              </>
            )}

            {feedback?.count > 0 && (
              <div data-testid="owner-feedback-list" className="border-t border-white/[0.06] pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-white font-medium">Client feedback</span>
                  {feedback.latest_decision === 'approve' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981]">Approved</span>}
                  {feedback.latest_decision === 'request_changes' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B]">Changes requested</span>}
                  <span className="text-[10px] text-[#55555C] ml-auto">{feedback.approvals} approvals · {feedback.changes} change requests</span>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {feedback.items.map(f => (
                    <div key={f.id} data-testid={`feedback-item-${f.id}`} className={`p-2.5 rounded-lg border ${f.resolved ? 'bg-[#10B981]/[0.05] border-[#10B981]/20' : 'bg-white/[0.02] border-white/[0.06]'}`}>
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-xs font-medium text-white">{f.client_name}</span>
                        {f.decision === 'approve' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981]">Approved</span>}
                        {f.decision === 'request_changes' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B]">Changes</span>}
                        {f.scene_title && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#00F0FF]/15 text-[#00F0FF]">📌 {f.scene_title}</span>}
                        {f.resolved && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981]">Resolved</span>}
                        <span className="text-[9px] text-[#55555C] ml-auto">{new Date(f.created_at).toLocaleDateString()}</span>
                      </div>
                      {f.message && <p className="text-xs text-[#C7C7CF] whitespace-pre-wrap break-words">{f.message}</p>}
                      {f.reply && (
                        <div className="mt-1.5 pl-2 border-l-2 border-[#FF4500]/40">
                          <p className="text-[9px] text-[#FF4500] font-semibold">Your reply</p>
                          <p className="text-xs text-[#C7C7CF] whitespace-pre-wrap break-words">{f.reply}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 mt-2">
                        <input data-testid={`reply-input-${f.id}`} value={replyDrafts[f.id] || ''}
                          onChange={e => setReplyDrafts(d => ({ ...d, [f.id]: e.target.value }))}
                          placeholder={f.reply ? 'Update reply…' : 'Reply to client…'}
                          className="flex-1 px-2 py-1 rounded-lg bg-[#0d0d11] border border-white/10 text-[11px] text-white" />
                        <button data-testid={`reply-btn-${f.id}`} onClick={() => handleReplyFeedback(f.id)}
                          className="px-2 py-1 rounded-lg bg-[#FF4500]/15 text-[#FF4500] hover:bg-[#FF4500]/25 border border-[#FF4500]/30 text-[10px] font-medium">Reply</button>
                        <button data-testid={`resolve-btn-${f.id}`} onClick={() => handleResolveFeedback(f.id, !f.resolved)}
                          className={`px-2 py-1 rounded-lg text-[10px] font-medium border ${f.resolved ? 'bg-white/[0.05] text-[#8A8A93] border-white/10' : 'bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30'}`}>
                          {f.resolved ? 'Reopen' : 'Resolve'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
